import { SelectField, SelectFieldProps } from '@nexelem/inputs';
import { useController } from 'react-hook-form';
import { MaxWidthProp, maxWidthVariants } from '../utils/maxWidthVariants';
import { cn } from '@nexelem/utils';

type Option = {
  id: string | number;
  label: string;
};

export type FormSelectIdProps<T extends Option = Option> = {
  name: string;
  options?: T[];
  maxWidth?: MaxWidthProp;
  showErrorMessage?: boolean;
  className?: string;
} & Pick<
  SelectFieldProps<T>,
  | 'isMultiple'
  | 'required'
  | 'readOnly'
  | 'label'
  | 'placeholder'
  | 'disableClear'
  | 'loading'
  | 'onInputChange'
  | 'renderOption'
  | 'inputValue'
  | 'enableVirtualKeyboard'
  | 'size'
>;

type FieldValues = {
  [key: string]: Option['id'] | Option['id'][];
};

export const FormSelectId = <T extends Option>({
  maxWidth = 320,
  name,
  options = [],
  showErrorMessage = true,
  className,
  ...props
}: FormSelectIdProps<T>) => {
  const { field, fieldState } = useController<FieldValues>({ name });

  const handleChange = (option: Option | Option[] | null) => {
    const value = Array.isArray(option) ? option.map((o) => o.id) : option?.id;
    field.onChange(value);
  };

  const findValues = options.filter(({ id }) =>
    Array.isArray(field.value) ? field.value.includes(id) : id === field.value,
  );

  const value = props.isMultiple ? findValues : findValues[0];

  const errorMessage = showErrorMessage ? fieldState.error?.message : undefined;

  return (
    <SelectField
      {...field}
      {...props}
      className={cn(maxWidthVariants({ maxWidth }), className)}
      error={!!fieldState.error}
      errorMessage={errorMessage}
      getOptionKey={({ id }) => id.toString()}
      options={options}
      showErrorBorder={!!fieldState.error}
      value={value}
      onChange={handleChange}
    />
  );
};
